/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useRoleProfileData.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for loading role profile data and menu items.
────────────────────────────────────────────────────────────────*/

import { useEffect, useRef, useState } from 'react';
import { useAuthStore } from '../../../store/auth';
import { useI18n } from '../../../hooks/useI18n';
import { useNotifications } from '../../../contexts/NotificationContext';
import { getMenuItemsFlat, MenuItemFlat } from '../../../config/menuDefinition';
import { fetchRoleProfileById } from '../api/roleProfilesApi';
import { RoleProfileFormData } from '../types';


export function useRoleProfileData(
  id: string | undefined,
  duplicateId: string | null,
  mode: 'create' | 'edit',
  setFormData: (data: RoleProfileFormData) => void,
  setOriginalData: (data: RoleProfileFormData) => void,
  resetParam?: string | null,
) {
  const { user } = useAuthStore();
  const { t } = useI18n();
  const { showError, showInfo } = useNotifications();
  const tenantSlug = user?.tenant?.slug || 'main';
  const isMainTenant = tenantSlug === 'main';

  const [menuItems, setMenuItems] = useState<MenuItemFlat[]>([]);
  const [menuItemsReady, setMenuItemsReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const profileLoadedRef = useRef<string | null>(null);
  const formResetRef = useRef<boolean>(false);
  const lastIdRef = useRef<string | undefined>(id);
  const resetParamProcessedRef = useRef<string | null>(null);
  const menuItemsRef = useRef<MenuItemFlat[]>([]);

  // Use refs for functions to avoid re-renders
  const setFormDataRef = useRef(setFormData);
  const setOriginalDataRef = useRef(setOriginalData);
  const showErrorRef = useRef(showError);
  const showInfoRef = useRef(showInfo);

  // Update refs when functions change
  useEffect(() => {
    setFormDataRef.current = setFormData;
    setOriginalDataRef.current = setOriginalData;
    showErrorRef.current = showError;
    showInfoRef.current = showInfo;
  }, [setFormData, setOriginalData, showError, showInfo]);

  // Update menuItemsRef when menuItems changes
  useEffect(() => {
    menuItemsRef.current = menuItems;
  }, [menuItems]);

  // Helper: collect all leaf slugs from menu items
  const collectLeafSlugs = (items: MenuItemFlat[]): string[] => {
    const slugs: string[] = [];
    items.forEach(item => {
      if (item.level === 3 && item.slug) {
        slugs.push(item.slug);
      } else if (item.level === 2 && item.slug) {
        const hasChildren = items.some(
          child => child.parentId === item.id && child.level === 3
        );
        if (!hasChildren) {
          slugs.push(item.slug);
        }
      }
    });
    return slugs;
  };

  // Load menu items from the hardcoded definition — synchronous, no API call needed
  useEffect(() => {
    const flat = getMenuItemsFlat(t, isMainTenant);
    setMenuItems(flat);
    menuItemsRef.current = flat;
    setMenuItemsReady(true);
  // Re-run only if the translation function identity changes (language switch)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  // Load profile data
  useEffect(() => {
    const loadProfile = async () => {
      const loadId = mode === 'edit' ? id : duplicateId;
      const currentId = id || 'new';
      const previousId = lastIdRef.current;
      const idChangedToNew = previousId && previousId !== 'new' && currentId === 'new';
      const hasResetParam = !!resetParam && resetParam !== resetParamProcessedRef.current;

      // If reset param is present and not yet processed, force reset
      if (hasResetParam) {
        formResetRef.current = false;
        profileLoadedRef.current = null;
        // Clear the lastIdRef to ensure we detect the change
        lastIdRef.current = undefined;
        // Mark this reset param as processed
        resetParamProcessedRef.current = resetParam;
      }

      if (!loadId || loadId === 'new') {
        // Reset the flag if we were previously loading a profile OR if ID changed to 'new' OR if reset param is present
        if (hasResetParam || idChangedToNew || profileLoadedRef.current !== null) {
          formResetRef.current = false;
        }
        profileLoadedRef.current = null;

        // Reset form when navigating to new (either initial load or after save)
        // Always reset if resetParam is present, or if formResetRef is false, or if ID changed to new
        if (hasResetParam || !formResetRef.current || idChangedToNew) {
          formResetRef.current = true;
          lastIdRef.current = currentId;

          // Only reset if menu items are loaded
          if (menuItemsRef.current.length > 0) {
            // Initialize all leaf modules with NONE for create mode
            const leafSlugs = collectLeafSlugs(menuItemsRef.current);

            const allPermissions = leafSlugs.map(slug => ({
              moduleSlug: slug,
              level: 'NONE' as const
            }));

            setFormDataRef.current({
              name: '',
              description: '',
              permissions: allPermissions
            });
            setOriginalDataRef.current({
              name: '',
              description: '',
              permissions: allPermissions.map(p => ({ ...p }))
            });
          }
        } else {
          lastIdRef.current = currentId;
        }
        return;
      }

      // Wait for menu items to be loaded before loading profile
      if (menuItemsRef.current.length === 0) return;

      // Reset the form reset flag when loading an actual profile
      formResetRef.current = false;

      // Prevent duplicate API calls
      if (profileLoadedRef.current === loadId) {
        lastIdRef.current = currentId;
        return;
      }

      profileLoadedRef.current = loadId;
      lastIdRef.current = currentId;

      try {
        setLoading(true);
        const profile = await fetchRoleProfileById(loadId, tenantSlug, user?.id);

        // Initialize all leaf modules with NONE, then override with existing permissions
        const leafSlugs = collectLeafSlugs(menuItemsRef.current);

        const allPermissions = leafSlugs.map(slug => {
          const existing = (profile.permissions || []).find(
            (p: { moduleSlug: string; level: string }) => p.moduleSlug === slug
          );
          return existing
            ? { moduleSlug: slug, level: existing.level }
            : { moduleSlug: slug, level: 'NONE' as const };
        });

        const loadedData: RoleProfileFormData = {
          name: mode === 'edit' ? profile.name : `${profile.name}_copy`,
          description: profile.description || '',
          permissions: allPermissions,
          is_system: mode === 'edit' ? profile.is_system : false
        };

        setFormDataRef.current(loadedData);
        setOriginalDataRef.current({
          name: loadedData.name,
          description: loadedData.description,
          permissions: allPermissions.map(p => ({ ...p }))
        });

        if (duplicateId) {
          showInfoRef.current(t('profiles.messages.duplicateHint'));
        }
      } catch (error) {
        showErrorRef.current(error instanceof Error ? error.message : t('profiles.failedToLoadProfile'));
        profileLoadedRef.current = null;
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, id, duplicateId, tenantSlug, user?.id, resetParam, menuItemsReady]);

  return {
    menuItems,
    loading,
  };
}
